import { CalendarIcon } from 'lucide-react';
import { DatePicker as AriaDatePicker, Dialog, type DatePickerProps as AriaDatePickerProps, type DateValue } from 'react-aria-components';
import { Button } from '../../../components/buttons/Button';
import { Calendar } from '../../../components/Calendar';
import { DateInput } from './DateField';
import { FieldGroup } from '../base-controls/FieldGroup';
import { cn } from '../../../components/utils';
import { Popover, composeRenderProps } from 'react-aria-components';
import type { BaseFieldProps } from '../../../components/types';
import { FieldDescription } from '../base-controls/FieldDescription';
import { FieldError } from '../base-controls/FieldError';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { tv } from 'tailwind-variants';
import { baseControlStyles } from '../base-controls/util';
import { convertFromDateFieldValue, convertToDateFieldValue } from '~/util/date';
import type { CalendarDate, CalendarDateTime, ZonedDateTime } from '@internationalized/date';

const controlStyles = tv({
	extend: baseControlStyles
});

export interface DatePickerFieldProps extends Omit<AriaDatePickerProps<DateValue>, 'value' | 'onChange'>, BaseFieldProps {
	value: Date | null;
	onChange: (value: Date | null) => void;
}

export function DatePickerField({ label, description, ...props }: DatePickerFieldProps) {
	const { base } = controlStyles();
	const calendarDateValue = props.value ? convertToDateFieldValue(props.value) : null;

	const onChange = (newValue: CalendarDate | CalendarDateTime | ZonedDateTime | null) => {
		console.log('DatePickerField onChange', newValue);
		props.onChange(newValue ? convertFromDateFieldValue(newValue) : null);
	};
	return (
		<AriaDatePicker
			{...props}
			onChange={onChange}
			value={calendarDateValue}
			className={composeRenderProps(props.className, className => cn(base(), className))}
			aria-label={label}
		>
			{!props.hideLabel ? <FieldLabelWithHtml className="mb-1" label={label} required={props.required} /> : null}
			<FieldGroup className="w-auto gap-1">
				<DateInput className="pointer-events-none select-none" />
				<Button Icon={CalendarIcon} appearance="outline" intent="neutralLight" className="mr-1" size="xs" />
			</FieldGroup>
			{description && <FieldDescription>{description}</FieldDescription>}
			{props.error && <FieldError {...props.error} />}
			<Popover className="max-w-[340px]">
				<Dialog className="bg-white">
					<Calendar />
				</Dialog>
			</Popover>
		</AriaDatePicker>
	);
}
